import { _ as _extends } from '../../_rollupPluginBabelHelpers-mO86zOh3.js';
import React, { forwardRef, useRef, useState, useEffect } from 'react';
import useId from '@indeed/ihl-use-id';
import { useDebouncedCallback } from 'use-debounce';
import { useMergeRefs } from '../../hooks/useMergeRefs.js';
import { useOutsideInteraction } from '../../hooks/useOutsideInteraction.js';
import { ListboxProvider } from '../Listbox/useListboxContext.js';
import { SuggestListInput } from './SuggestListInput.js';
import { useListbox } from '../Listbox/useListbox.js';
import escapeRegExp from '../../utils/escapeRegExp.js';
import { jsx } from '@emotion/react';
import { Popup } from '../Popup/Popup.js';
import { InteractiveList } from '../InteractiveList/InteractiveList.js';
import { Box } from '../Box/Box.js';
import { Text } from '../Text/Text.js';
import { Portal } from '../Portal/Portal.js';
import '@indeed/ifl-css';
import '@indeed/ifl-icons';
import '../Input/InputWrapper.js';
import '../../utils/transition.js';
import '../../utils/styled.js';
import '../TouchTarget/TouchTarget.js';
import '../Input/Input.js';
import '../../utils/createResponsive.js';
import '../../constants/index.js';
import '../../utils/mergeRefs.js';
import '../../hooks/useHasFocus.js';
import '../../utils/warn.js';
import '../../hooks/useCheckValidity.js';
import '../Input/ChipInput.js';
import '../Input/styles.js';
import '../Input/AutosizeInput.js';
import '../../hooks/useIsomorphicLayoutEffect.js';
import '../Chip/ChipGroup.js';
import '../Chip/Chip.js';
import '@emotion/is-prop-valid';
import '../../hooks/useResponsiveValue.js';
import '../../hooks/useBreakpointIndex.js';
import '../Button/Button.js';
import '../../utils/transparentize.js';
import '../Button/context.js';
import '../Spinner/Spinner.js';
import '../IconWrapper/IconWrapper.js';
import '../Label/Label.js';
import '../VisuallyHidden/VisuallyHidden.js';
import '../Flex/Flex.js';
import '../../utils/displayName.js';
import '../Listbox/useSelectableCollectionKeyboard.js';
import '../Listbox/useTypeKeySearch.js';
import '../../hooks/useCollection.js';
import '../../hooks/useControllableState.js';
import '@floating-ui/react-dom';
import 'react-dom';
import '../IndeedThemeProvider/IndeedThemeProvider.js';
import '../IndeedThemeProvider/utils.js';
import '../IndeedThemeProvider/theme.js';
import 'deepmerge';
import '@indeed/ifl-themes';
import '../LinkProvider/LinkProvider.js';

/**
 * @see https://experience.indeed.design/components/ids/suggest-list
 */
const SuggestList = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    children,
    contentAfter,
    contentBefore,
    disabled = false,
    disableTypeToFocus = false,
    invalid = false,
    onOpenChange,
    onOptionChoice,
    placeholder,
    popupSx,
    portaledPopup = false,
    toggleButtonProps,
    size,
    onChange,
    hideToggleButton,
    noSuggestionsMessage,
    ...remainingProps
  } = props;
  const hasNoChildren = React.Children.count(children) < 1;
  const listboxId = `Listbox-${useId()}`;
  const messageId = `No-Suggestions-${useId()}`;
  const listboxRef = useRef(null);
  const inputRef = useRef(null);
  const popupRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const mergedInputRef = useMergeRefs(forwardedRef, inputRef);
  const [popupAnchor, setPopupAnchor] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(props.defaultValue || props.value || "");
  useEffect(() => {
    if (props.value) {
      setInternalValue(props.value);
    }
  }, [props.value]);

  // useListbox setup
  const {
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    focusVisible,
    setFocusVisible,
    initializeVirtualFocus,
    onKeyDown: onListboxKeyDown,
    collectionState
  } = useListbox({
    scrollContainerRef: popupRef,
    onSelectionChange: selectedValues => {
      if (onOptionChoice) {
        onOptionChoice(selectedValues[0]);
      }
      if (selectedValues.length > 0) {
        setInternalValue(selectedValues[0]);
        closePopup();
      }
    },
    multiselect: false
  });
  const canOpen = !hasNoChildren || noSuggestionsMessage !== undefined;

  // item lookup
  const debouncedItemLookup = useDebouncedCallback(value => {
    const matchedItems = collectionState.getItemsByPredicate(item => {
      let search = value;

      // Escape all special characters in search string
      search = escapeRegExp(search);
      const regex = new RegExp(`^${search}`, "gi");
      return item && item.value && typeof item.value === "string" ? regex.test(item.value) : false;
    });
    if (matchedItems.length && !disableTypeToFocus) {
      setActiveOption(matchedItems[0]);
    } else {
      setActiveOption(undefined);
    }
  }, 100);

  // Closes popup
  const closePopup = () => {
    setIsOpen(false);
    if (onOpenChange) {
      onOpenChange(false);
    }
    setActiveOption(undefined);
    // reset focus visible when the popup is closed
    setFocusVisible(false);
  };

  // Opens popup
  const openPopup = () => {
    inputRef.current?.focus(); // Ensure focus stays on input when toggle button is clicked
    setIsOpen(true);
    if (onOpenChange) {
      onOpenChange(true);
    }
  };

  // Closes popup on outside interaction, excluding the trigger
  const handleOutsideInteraction = () => {
    if (isOpen) {
      closePopup();
    }
  };
  useOutsideInteraction({
    refs: [popupRef, inputRef, toggleButtonRef],
    onOutsideInteraction: handleOutsideInteraction,
    disabled: !isOpen
  });
  const handleOnClick = event => {
    if (!isOpen && canOpen) {
      openPopup();
    }
    remainingProps.onClick?.(event);
  };

  // keyboard handler
  const handleOnKeyDown = event => {
    // Using `nativeEvent` due to: facebook/react#13104
    if (event.nativeEvent.isComposing) {
      return;
    }
    if (!isOpen) {
      if (event.key === "ArrowUp" || event.key === "ArrowDown" ||
      // any character and backspace
      event.key.length === 1 || event.key === "Backspace") {
        openPopup();
        if (!disableTypeToFocus || event.key === "ArrowUp" || event.key === "ArrowDown") {
          initializeVirtualFocus();
        }
      }
    }
    if (isOpen) {
      setFocusVisible(true);
      if (event.key === "Escape" || event.key === "Tab") {
        event.stopPropagation();
        closePopup();
      }
      if (disableTypeToFocus &&
      // Allow Enter to submit forms or perform any other intended function
      // if no options are active and type to focus is disabled
      event.key === "Enter" && activeOption === undefined) {
        event.stopPropagation();
        closePopup();
      } else {
        onListboxKeyDown(event);
      }
    }
    remainingProps.onKeyDown?.(event);
  };
  const handleOnChange = event => {
    const value = event.target.value;
    setInternalValue(value);
    debouncedItemLookup(value);
    onChange?.(event);
  };

  // Listbox context
  const context = {
    selectionType: "none",
    activeOption,
    setActiveOption,
    selectOption,
    selectedOptionValues,
    registerOption: collectionState.registerItem,
    focusVisible,
    setFocusVisible,
    withCheckmark: false
  };

  // Popup to render inline or in a portal
  const SuggestListPopup = jsx(Popup, {
    ref: popupRef,
    alignment: "start",
    anchor: popupAnchor,
    onMouseDown: e => e.preventDefault() // Prevent focus from being sent to body if clicking on non-interactive elements
    ,
    sx: {
      overflowY: "auto",
      overflowX: "hidden",
      display: isOpen && canOpen ? "block" : "none",
      paddingBlock: 3,
      paddingInline: 3,
      maxBlockSize: "262px",
      // Magic number to ensure last item is always partially shown to indicate scrollability
      inlineSize: popupAnchor?.offsetWidth,
      zIndex: 1,
      ...popupSx
    }
  }, jsx(ListboxProvider, {
    value: context
  }, jsx(InteractiveList, {
    role: "listbox",
    id: listboxId,
    ref: listboxRef
  }, children)), hasNoChildren && noSuggestionsMessage ? jsx(Box, {
    sx: {
      paddingBlock: 3
    }
  }, jsx(Text, {
    variant: "secondary",
    sx: {
      paddingBlock: "sc3",
      paddingInline: 3
    },
    id: messageId
  }, noSuggestionsMessage)) : null);
  return jsx(Box, {
    sx: {
      boxSizing: "border-box",
      position: "relative",
      inlineSize: "100%"
    },
    "aria-owns": portaledPopup && isOpen ? listboxId : undefined
  }, jsx(SuggestListInput, _extends({
    ref: mergedInputRef,
    toggleButtonRef: toggleButtonRef,
    type: "text",
    autoComplete: "off",
    role: "combobox",
    "aria-activedescendant": isOpen ? activeOption?.id : undefined,
    "aria-controls": listboxId,
    "aria-expanded": isOpen && canOpen,
    placeholder: placeholder,
    contentAfter: contentAfter,
    contentBefore: contentBefore,
    disabled: disabled,
    invalid: invalid,
    isOpen: isOpen,
    canOpen: canOpen,
    value: internalValue,
    size: size,
    setPopupAnchor: setPopupAnchor,
    hideToggleButton: hideToggleButton,
    toggleButtonProps: toggleButtonProps,
    openPopup: openPopup,
    closePopup: closePopup
  }, remainingProps, {
    onClick: handleOnClick,
    onChange: handleOnChange,
    onKeyDown: handleOnKeyDown,
    messageId: isOpen && hasNoChildren && noSuggestionsMessage ? messageId : undefined
  })), portaledPopup ? jsx(Portal, null, SuggestListPopup) : SuggestListPopup);
});
SuggestList.displayName = "SuggestList";

export { SuggestList };
